import React, { useEffect, useState } from "react";
import get from "lodash/get";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import textUtils from "../../../common/utilities/TextUtils";
import { TYPES_OPTIONS } from "../../../../settings/constants/types";
import { AGGREGATE_OPERATION_TYPE } from "../../../../settings/constants/aggregateOperation";
import { toast } from "react-toastify";

const INITIAL_STATE = {
    id: null,
    name: "",
    type: "",
    comment: "",
};

// Helper function to find nested attributes
const findNestedAttributes = (attributes, path) => {
    if (!path.length) return attributes;
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return rest.length ? findNestedAttributes(currentAttr.attributes, rest) : currentAttr.attributes;
};

// Helper function to get attribute path names
const getAttributePathNames = (attributes, path) => {
    if (!path.length) return [];
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return [
        { index: current, name: currentAttr.name || `Attribute ${current + 1}` },
        ...getAttributePathNames(currentAttr.attributes, rest)
    ];
};

// Helper function to update nested attributes
const updateNestedAttributes = (attributes, path, newAttributes) => {
    if (!path.length) return newAttributes;
    const [current, ...rest] = path;
    const newArray = [...attributes];
    if (rest.length) {
        newArray[current] = {
            ...newArray[current],
            attributes: updateNestedAttributes(newArray[current].attributes, rest, newAttributes)
        };
    } else {
        newArray[current] = {
            ...newArray[current],
            attributes: newAttributes
        };
    }
    return newArray;
};

// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};

const BreadcrumbNav = ({ pathNames, onNavigate }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3">
                <li className="breadcrumb-item">
                    <Button
                        variant="link"
                        className="p-0"
                        onClick={() => onNavigate([])}
                    >
                        Root
                    </Button>
                </li>
                {pathNames.map((item, index) => (
                    <li key={index} className="breadcrumb-item">
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => onNavigate(pathNames.slice(0, index + 1).map(p => p.index))}
                        >
                            {item.name}
                        </Button>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

const AttributeEditor = ({ attributes = [], onChange, currentPath = [], onPathChange }) => {
    const currentAttributes = findNestedAttributes(attributes || [], currentPath) || [];
    const pathNames = getAttributePathNames(attributes || [], currentPath);

    const handleAddAttribute = () => {
        const newAttributes = [...currentAttributes, { name: "", type: "", comment: "", attributes: [] }];
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleRemoveAttribute = (index) => {
        const newAttributes = [...currentAttributes];
        newAttributes.splice(index, 1);
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...currentAttributes];
        newAttributes[index] = { ...newAttributes[index], [field]: value };
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleNavigateToNested = (index) => {
        onPathChange([...currentPath, index]);
    };

    // More defensive check for canAddAttribute
    const canAddAttribute = Array.isArray(currentAttributes) && 
        (currentAttributes.length === 0 || currentAttributes.every(attr => attr && isValidAttributeName(attr.name)));

    return (
        <div>
            {currentPath.length > 0 && (
                <BreadcrumbNav
                    pathNames={pathNames}
                    onNavigate={onPathChange}
                />
            )}
            <div className="mb-3">
                {Array.isArray(currentAttributes) && currentAttributes.map((attr, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className={`form-control ${!isValidAttributeName(attr?.name) ? 'is-invalid' : ''}`}
                                    placeholder="Attribute Name"
                                    value={attr?.name || ""}
                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                />
                                {!isValidAttributeName(attr?.name) && (
                                    <div className="invalid-feedback">
                                        Attribute name is required
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <Select
                                    options={TYPES_OPTIONS}
                                    value={{ label: attr?.type || "", value: attr?.type || "" }}
                                    onChange={(e) => handleAttributeChange(index, "type", e.value)}
                                    className="react-select-container"
                                />
                            </div>
                            <div className="col-md-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveAttribute(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-8">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Comment (optional)"
                                    value={attr?.comment || ""}
                                    onChange={(e) => handleAttributeChange(index, "comment", e.target.value)}
                                />
                            </div>
                        </div>
                        {(attr?.type === "OBJECT" || attr?.type === "ARRAYOFOBJ") && (
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleNavigateToNested(index)}
                                    disabled={!isValidAttributeName(attr?.name)}
                                >
                                    Edit Nested Attributes ({attr?.attributes?.length || 0})
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                >
                    Add Attribute
                </Button>
            </div>
        </div>
    );
};

const AddResponseParameter = ({
    isOpen = false,
    row = null,
    onSuccessCallBack,
    onClose,
}) => {
    const [parameterObj, setParameterObj] = useState({ ...INITIAL_STATE });
    const [currentPath, setCurrentPath] = useState([]);

    useEffect(() => {
        if (isOpen) {
            if (row) {
                setParameterObj({
                    id: row.id,
                    name: get(row, "name", ""),
                    type: get(row, "type", ""),
                    comment: get(row, "comment", ""),
                    attributes: get(row, "attributes", []),
                });
            } else {
                setParameterObj({ ...INITIAL_STATE });
            }
            setCurrentPath([]);
        }
    }, [isOpen, row]);

    const handleTextChange = (key, value) => {
        setParameterObj((prev) => ({ ...prev, [key]: value }));
    };

    const handleSaveParameter = () => {
        if (
            textUtils.isEmpty(parameterObj.name, "Name", true) ||
            textUtils.isEmpty(parameterObj.type, "Type", true)
        ) {
            return false;
        }

        // Validate attributes if type is COLLECTION_OBJECT or COLLECTION_OBJECT_LIST
        if ((parameterObj.type === "ARRAYOFOBJ" || parameterObj.type === "OBJECT") &&
            (!parameterObj.attributes || parameterObj.attributes.length === 0)) {
            toast.error("Please add at least one attribute for the collection object");
            return false;
        }

        // Validate all attribute names and collection objects
        const validateAttributes = (attrs) => {
            if (!Array.isArray(attrs)) return true;
            
            return attrs.every(attr => {
                if (!attr) return true;

                // Check if attribute name is valid
                if (!isValidAttributeName(attr.name)) {
                    toast.error(`Attribute "${attr.name || 'Unnamed'}" must have a valid name`);
                    return false;
                }

                // Check if collection objects have at least one inner attribute
                if ((attr.type === "OBJECT" || attr.type === "ARRAYOFOBJ")) {
                    if (!attr.attributes || attr.attributes.length === 0) {
                        toast.error(`Collection object "${attr.name}" must have at least one inner attribute`);
                        return false;
                    }
                    // Recursively validate nested attributes
                    return validateAttributes(attr.attributes);
                }

                return true;
            });
        };

        // Always validate attributes if they exist, regardless of the main type
        if (parameterObj.attributes && parameterObj.attributes.length > 0) {
            if (!validateAttributes(parameterObj.attributes)) {
                return false;
            }
        }

        const parameterToSave = parameterObj;
        if (!(parameterObj?.id?.length > 0)) {
            delete parameterToSave.id;
        }
        onSuccessCallBack(parameterToSave);
        onClose();
    };

    return (
        <Modal size="lg" show={isOpen} onHide={onClose} className="add-response-parameter-modal add-more-form">
            <Modal.Header closeButton>
                <Modal.Title>{`${parameterObj?.id ? "Edit" : "Add"} Response Parameter`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Name *</label>
                        <input
                            type="text"
                            className="form-control input-default"
                            placeholder="Name"
                            onChange={(e) => handleTextChange("name", e.target.value)}
                            value={parameterObj.name}
                        />
                    </div>
                </div>
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Type *</label>
                        <Select
                            options={TYPES_OPTIONS}
                            value={{ label: parameterObj.type, value: parameterObj.type }}
                            id="type"
                            name="type"
                            onChange={(e) => handleTextChange("type", e.value)}
                            className="react-select-container"
                        />
                    </div>
                </div>
                {(parameterObj.type === "ARRAYOFOBJ" || parameterObj.type === "OBJECT") && (
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Attributes *</label>
                            <AttributeEditor
                                attributes={parameterObj.attributes || []}
                                onChange={(attributes) => handleTextChange("attributes", attributes)}
                                currentPath={currentPath}
                                onPathChange={setCurrentPath}
                            />
                        </div>
                    </div>
                )}
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Comment </label>
                        <input
                            type="text"
                            className="form-control input-default"
                            placeholder="Comment"
                            onChange={(e) => handleTextChange("comment", e.target.value)}
                            value={parameterObj.comment}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSaveParameter}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddResponseParameter;
